import {TemplateNode} from "#template/template-node/template-node"
import {TemplateListNode} from "#template/template-list-node/template-list-node"
import {TemplateGraph} from "#template/nodes/template-graph/template-graph"
import {TemplateReference} from "#template/nodes/template-graph/template-reference"
import {ConfigGroup} from "#template/nodes/owner/config-group"
import {ConfigVariant} from "#template/nodes/owner/config-variant"
import {Group} from "#template/nodes/owner/group"
import {TemplateInstance} from "#template/nodes/object/template-instance"
import {SwitchNode} from "#template/fundamental/switch/switch-node"
import {InputNode} from "#template/fundamental/input/input-node"
import {OutputNode} from "#template/fundamental/output/output-node"
import {ExportNode} from "#template/fundamental/export/export-node"
import {wrapInterfaceId} from "#template/interface-descriptors"
import {isActivationVisitorNode, isMaterialAssignmentLikeNode, isNodeOwnerLikeNode} from "#template/role-markers"
import {Render} from "#template/nodes/render/render"
import {PostProcessRender} from "#template/nodes/render/post-process-render"
import {HdriLight} from "#template/nodes/render/hdri-light"
import {SceneProperties} from "#template/nodes/render/scene-properties"
import {ObjectNode} from "#template/nodes/object/object-node"
import {RigidRelation} from "#template/nodes/rigid-relation/rigid-relation"
import {isMaterialLikeNode} from "#template/nodes/material/marker"

export class UnsupportedVariationStructureError extends Error {
    constructor(reason: string) {
        super(`Template structure not supported by the fast variation analysis: ${reason}`)
        this.name = "UnsupportedVariationStructureError"
    }
}

export interface TemplateGraphLoader {
    loadTemplateGraph(templateId: number, templateRevisionId: number | null): Promise<TemplateGraph>
}

type ParentLink = {context: AnalysisContext; instance: TemplateInstance}

class AnalysisContext {
    readonly nodes = new Set<TemplateNode>()
    readonly childContexts = new Map<TemplateInstance, AnalysisContext[]>()

    constructor(
        readonly graph: TemplateGraph,
        readonly prefix: string | null,
        readonly parent: ParentLink | null,
    ) {
        const visit = (node: TemplateNode) => {
            if (this.nodes.has(node)) return
            this.nodes.add(node)
            for (const child of node.children) {
                if (!(child instanceof TemplateNode)) continue
                // Graphs other than our own root belong to a sub-template context.
                if (child instanceof TemplateGraph && child !== graph) continue
                visit(child)
            }
        }
        visit(graph)
    }

    keyOf(id: string) {
        return this.prefix === null ? id : wrapInterfaceId(this.prefix, id)
    }

    claimedValue(inputId: string): {claimed: boolean; value: unknown} {
        if (!this.parent) return {claimed: false, value: undefined}
        const value = this.parent.instance.parameters.parameters.parameters[inputId]
        if (value === undefined || value === null) return {claimed: false, value: undefined}
        return {claimed: true, value}
    }
}

export type KeyOccurrence = {
    key: string
    derived: boolean
    group: ConfigGroup
}

// Over-approximates, for every config key, the set of free keys whose selection can change whether the key exists, which
// variants it offers or (for keys a parent claims) which value it has. Over-approximation only costs speed, never exactness.
export class VariationStructure {
    private constructor(
        readonly occurrences: readonly KeyOccurrence[],
        private readonly controllersByKey: ReadonlyMap<string, ReadonlySet<string>>,
        private readonly gateKeys: ReadonlySet<string>,
        private readonly influenceByKey: ReadonlyMap<string, ReadonlySet<string>>,
        private readonly sceneSettingsKeys: ReadonlySet<string>,
        private readonly sceneObjectsByKey: ReadonlyMap<string, ReadonlySet<string>>,
    ) {}

    static async analyze(rootGraph: TemplateGraph, loader: TemplateGraphLoader): Promise<VariationStructure> {
        const analysis = new StructureAnalysis(loader)
        await analysis.run(rootGraph)
        return analysis.result()
    }

    static fromParts(
        occurrences: readonly KeyOccurrence[],
        controllersByKey: ReadonlyMap<string, ReadonlySet<string>>,
        gateKeys: ReadonlySet<string>,
        influenceByKey: ReadonlyMap<string, ReadonlySet<string>>,
        sceneSettingsKeys: ReadonlySet<string>,
        sceneObjectsByKey: ReadonlyMap<string, ReadonlySet<string>> = new Map(),
    ) {
        return new VariationStructure(occurrences, controllersByKey, gateKeys, influenceByKey, sceneSettingsKeys, sceneObjectsByKey)
    }

    isGate(key: string) {
        return this.gateKeys.has(key)
    }

    affectsSceneSettings(key: string) {
        return this.sceneSettingsKeys.has(key)
    }

    // Names of the scene objects whose geometry, transform, visibility or lighting the key can change, alone or together
    // with other keys; material subtrees are left out.
    sceneObjectsFedBy(key: string): readonly string[] {
        return [...(this.sceneObjectsByKey.get(key) ?? [])]
    }

    controllers(key: string): ReadonlySet<string> {
        return this.controllersByKey.get(key) ?? emptySet
    }

    influence(key: string): ReadonlySet<string> {
        return this.influenceByKey.get(key) ?? new Set([key])
    }

    isKnownKey(key: string) {
        return this.controllersByKey.has(key)
    }
}

const emptySet: ReadonlySet<string> = new Set()

class StructureAnalysis {
    private readonly contexts: AnalysisContext[] = []
    private readonly activationMemo = new Map<AnalysisContext, Map<TemplateNode, Set<string>>>()
    private readonly valueMemo = new Map<AnalysisContext, Map<TemplateNode, Set<string>>>()
    private readonly sceneMemo = new Map<AnalysisContext, Map<TemplateNode, Set<string>>>()
    private readonly inProgress = new Set<string>()
    private readonly contextIds = new Map<AnalysisContext, number>()

    constructor(private readonly loader: TemplateGraphLoader) {}

    async run(rootGraph: TemplateGraph) {
        await this.buildContext(rootGraph, null, null, [])
    }

    private async buildContext(graph: TemplateGraph, prefix: string | null, parent: ParentLink | null, graphChain: TemplateGraph[]) {
        if (graphChain.includes(graph)) throw new UnsupportedVariationStructureError("circular template instancing")
        const context = new AnalysisContext(graph, prefix, parent)
        this.contextIds.set(context, this.contexts.length)
        this.contexts.push(context)

        // Requested together so the loader can fetch sub-templates in parallel.
        const instances = [...context.nodes].filter((node): node is TemplateInstance => node instanceof TemplateInstance)
        await Promise.all(
            instances.map(async (instance) => {
                const graphs = await this.resolveTemplates(instance.parameters.template, context, [])
                const childPrefix = context.keyOf(instance.parameters.id)
                const children = await Promise.all(
                    graphs.map((childGraph) => this.buildContext(childGraph, childPrefix, {context, instance}, [...graphChain, graph])),
                )
                context.childContexts.set(instance, children)
            }),
        )
        return context
    }

    private async resolveTemplates(templateLike: unknown, context: AnalysisContext, seen: unknown[]): Promise<TemplateGraph[]> {
        if (templateLike === null || templateLike === undefined) return []
        if (seen.includes(templateLike)) throw new UnsupportedVariationStructureError("circular template expression")
        const nextSeen = [...seen, templateLike]
        if (templateLike instanceof TemplateGraph) return [templateLike]
        if (templateLike instanceof TemplateReference) {
            const {templateId, templateRevisionId} = templateLike.parameters
            return [await this.loader.loadTemplateGraph(templateId, templateRevisionId)]
        }
        if (templateLike instanceof SwitchNode) {
            const candidates = templateLike.parameters.nodes.parameters.list
            return (await Promise.all(candidates.map((candidate) => this.resolveTemplates(candidate, context, nextSeen)))).flat()
        }
        if (templateLike instanceof InputNode) {
            const {claimed, value} = context.claimedValue(templateLike.parameters.id)
            const graphs: TemplateGraph[] = []
            if (claimed && context.parent) graphs.push(...(await this.resolveTemplates(value, context.parent.context, nextSeen)))
            graphs.push(...(await this.resolveTemplates(templateLike.parameters.default, context, nextSeen)))
            return graphs
        }
        throw new UnsupportedVariationStructureError(`template chosen by ${(templateLike as TemplateNode).nodeClass ?? typeof templateLike}`)
    }

    result(): VariationStructure {
        const occurrences: KeyOccurrence[] = []
        const controllersByKey = new Map<string, Set<string>>()

        for (const context of this.contexts) {
            for (const node of context.nodes) {
                if (!(node instanceof ConfigGroup)) continue
                this.checkHostedByModelledOwners(node, context, node)
                const key = context.keyOf(node.parameters.id)
                const {claimed, value} = context.claimedValue(node.parameters.id)
                occurrences.push({key, derived: claimed, group: node})

                const controllers = new Set(this.activationKeys(node, context))
                if (claimed && context.parent) for (const k of this.valueKeys(value, context.parent.context)) controllers.add(k)

                const existing = controllersByKey.get(key)
                if (existing) for (const k of controllers) existing.add(k)
                else controllersByKey.set(key, controllers)
            }
        }

        const freeKeys = new Set(occurrences.filter((o) => !o.derived).map((o) => o.key))
        for (const [key, controllers] of controllersByKey) {
            if (controllers.has(key) && freeKeys.has(key)) throw new UnsupportedVariationStructureError(`config group "${key}" controls itself`)
        }

        const gateKeys = new Set<string>()
        const influenceByKey = new Map<string, Set<string>>()
        for (const key of freeKeys) influenceByKey.set(key, new Set([key]))
        for (const [key, controllers] of controllersByKey) {
            for (const controller of controllers) {
                if (!freeKeys.has(controller) || controller === key) continue
                gateKeys.add(controller)
                influenceByKey.get(controller)!.add(key)
            }
        }

        const sceneSettingsKeys = new Set<string>()
        for (const context of this.contexts) {
            for (const node of context.nodes) {
                if (!(node instanceof Render || node instanceof PostProcessRender)) continue
                for (const key of this.valueKeys(node, context)) if (freeKeys.has(key)) sceneSettingsKeys.add(key)
            }
        }

        const sceneObjectsByKey = new Map<string, Set<string>>()
        for (const context of this.contexts) {
            for (const node of context.nodes) {
                if (!isSceneRoot(node)) continue
                const name = (node.parameters as {name?: unknown}).name
                for (const key of this.sceneKeys(node, context)) {
                    if (!freeKeys.has(key)) continue
                    const objects = sceneObjectsByKey.get(key) ?? new Set<string>()
                    objects.add(typeof name === "string" && name !== "" ? name : node.nodeClass)
                    sceneObjectsByKey.set(key, objects)
                }
            }
        }

        return VariationStructure.fromParts(occurrences, controllersByKey, gateKeys, influenceByKey, sceneSettingsKeys, sceneObjectsByKey)
    }

    private activationKeys(node: TemplateNode, context: AnalysisContext): Set<string> {
        return this.memoized(this.activationMemo, "act", node, context, () => {
            const keys = new Set<string>()
            if (node === context.graph) {
                const parent = context.parent
                if (parent) {
                    addAll(keys, this.activationKeys(parent.instance, parent.context))
                    addAll(keys, this.valueKeys(parent.instance.parameters.template, parent.context))
                }
                return keys
            }

            for (const owner of node.parents) {
                if (!(owner instanceof TemplateNode) || !context.nodes.has(owner)) continue

                if (owner === context.graph) {
                    addAll(keys, this.activationKeys(owner, context))
                } else if (owner instanceof TemplateListNode) {
                    for (const listOwner of owner.parents) {
                        if (!(listOwner instanceof TemplateNode) || !context.nodes.has(listOwner)) continue
                        addAll(keys, this.listEntryActivationKeys(node, owner, listOwner, context))
                    }
                } else if (isUnmodelledVisitor(owner)) {
                    addAll(keys, this.activationKeys(owner, context))
                }
            }
            return keys
        })
    }

    private listEntryActivationKeys(entry: TemplateNode, list: TemplateListNode<TemplateNode>, listOwner: TemplateNode, context: AnalysisContext): Set<string> {
        const keys = new Set<string>()
        if (listOwner instanceof ConfigGroup) {
            if (listOwner.parameters.nodes !== list) return keys
            addAll(keys, this.activationKeys(listOwner, context))
            if (entry instanceof ConfigVariant) addAll(keys, this.selectionKeys(listOwner, context))
        } else if (listOwner instanceof Group) {
            if (listOwner.parameters.nodes !== list) return keys
            addAll(keys, this.activationKeys(listOwner, context))
            addAll(keys, this.valueKeys(listOwner.parameters.active, context))
        } else if (listOwner instanceof ConfigVariant) {
            if (listOwner.parameters.nodes !== list) return keys
            addAll(keys, this.activationKeys(listOwner, context))
        } else if (listOwner === context.graph || listOwner instanceof TemplateListNode) {
            addAll(keys, this.activationKeys(list, context))
        } else if (isUnmodelledVisitor(listOwner)) {
            addAll(keys, this.activationKeys(listOwner, context))
        }
        return keys
    }

    // Only the modelled owners decide group existence in a way the analysis follows; a group inside any other visitor
    // (e.g. a mattress component, compiled in its own pass) could exist under conditions the analysis cannot see.
    private checkHostedByModelledOwners(node: TemplateNode, context: AnalysisContext, group: ConfigGroup) {
        const fail = (host: TemplateNode) => {
            throw new UnsupportedVariationStructureError(`config group "${group.parameters.id}" sits inside ${host.nodeClass}`)
        }
        for (const parent of node.parents) {
            if (!(parent instanceof TemplateNode) || !context.nodes.has(parent) || parent === context.graph) continue
            if (isUnmodelledVisitor(parent)) fail(parent)
            if (!(parent instanceof TemplateListNode)) continue
            for (const listOwner of parent.parents) {
                if (!(listOwner instanceof TemplateNode) || !context.nodes.has(listOwner) || listOwner === context.graph) continue
                if (listOwner instanceof ConfigGroup || listOwner instanceof ConfigVariant || listOwner instanceof Group) {
                    this.checkHostedByModelledOwners(listOwner, context, group)
                } else if (listOwner instanceof TemplateListNode) {
                    this.checkHostedByModelledOwners(parent, context, group)
                } else if (isUnmodelledVisitor(listOwner)) {
                    fail(listOwner)
                }
            }
        }
    }

    private selectionKeys(group: ConfigGroup, context: AnalysisContext): Set<string> {
        const {claimed, value} = context.claimedValue(group.parameters.id)
        if (claimed && context.parent) return this.valueKeys(value, context.parent.context)
        return new Set([context.keyOf(group.parameters.id)])
    }

    private valueKeys(value: unknown, context: AnalysisContext): Set<string> {
        if (Array.isArray(value)) {
            const keys = new Set<string>()
            for (const item of value) addAll(keys, this.valueKeys(item, context))
            return keys
        }
        if (!(value instanceof TemplateNode)) return new Set()
        const node = value
        if (node instanceof TemplateGraph || node instanceof TemplateReference) return new Set()

        return this.memoized(this.valueMemo, "val", node, context, () => {
            const keys = new Set<string>()
            if (node instanceof SwitchNode) {
                for (const candidate of node.parameters.nodes.parameters.list) {
                    addAll(keys, this.activationKeys(candidate, context))
                    addAll(keys, this.valueKeys(candidate, context))
                }
                return keys
            }
            if (node instanceof InputNode) {
                const inputId = node.parameters.id
                keys.add(context.keyOf(inputId))
                const {claimed, value: claimedValue} = context.claimedValue(inputId)
                if (claimed && context.parent) addAll(keys, this.valueKeys(claimedValue, context.parent.context))
                addAll(keys, this.valueKeys(node.parameters.default, context))
                return keys
            }
            if (node instanceof OutputNode) {
                const instance = node.parameters.template
                addAll(keys, this.activationKeys(instance, context))
                addAll(keys, this.valueKeys(instance.parameters.template, context))
                addAll(keys, this.valueKeys(Object.values(instance.parameters.parameters.parameters), context))
                for (const child of context.childContexts.get(instance) ?? []) addAll(keys, this.allKeysBelow(child))
                return keys
            }
            // Some nodes consult the active node set while evaluating, so being inside a variant can change a value.
            addAll(keys, this.activationKeys(node, context))
            for (const child of node.children) {
                if (child instanceof TemplateNode) addAll(keys, this.valueKeys(child, context))
            }
            return keys
        })
    }

    // Like valueKeys, but a material or a material assignment never changes what the scene contains, only how it looks.
    private sceneKeys(value: unknown, context: AnalysisContext): Set<string> {
        if (Array.isArray(value)) {
            const keys = new Set<string>()
            for (const item of value) addAll(keys, this.sceneKeys(item, context))
            return keys
        }
        if (!(value instanceof TemplateNode)) return new Set()
        const node = value
        if (node instanceof TemplateGraph || node instanceof TemplateReference) return new Set()
        if (isMaterialLikeNode(node) || isMaterialAssignmentLikeNode(node)) return new Set()

        return this.memoized(this.sceneMemo, "scene", node, context, () => {
            const keys = new Set<string>()
            if (node instanceof SwitchNode) {
                for (const candidate of node.parameters.nodes.parameters.list) {
                    addAll(keys, this.activationKeys(candidate, context))
                    addAll(keys, this.sceneKeys(candidate, context))
                }
                return keys
            }
            if (node instanceof InputNode) {
                const inputId = node.parameters.id
                keys.add(context.keyOf(inputId))
                const {claimed, value: claimedValue} = context.claimedValue(inputId)
                if (claimed && context.parent) addAll(keys, this.sceneKeys(claimedValue, context.parent.context))
                addAll(keys, this.sceneKeys(node.parameters.default, context))
                return keys
            }
            if (node instanceof OutputNode) {
                const instance = node.parameters.template
                addAll(keys, this.activationKeys(instance, context))
                addAll(keys, this.valueKeys(instance.parameters.template, context))
                for (const child of context.childContexts.get(instance) ?? []) {
                    for (const exported of child.nodes) {
                        if (exported instanceof ExportNode && exported.parameters.id === node.parameters.outputId) {
                            addAll(keys, this.sceneKeys(exported.parameters.node, child))
                        }
                    }
                }
                return keys
            }
            addAll(keys, this.activationKeys(node, context))
            // Values passed into a sub-template count where its own scene objects read them, through its inputs.
            const passedValues = node instanceof TemplateInstance ? node.parameters.parameters : undefined
            for (const child of node.children) {
                if (child instanceof TemplateNode && child !== passedValues) addAll(keys, this.sceneKeys(child, context))
            }
            return keys
        })
    }

    private allKeysBelow(context: AnalysisContext): Set<string> {
        const keys = new Set<string>()
        for (const node of context.nodes) if (node instanceof ConfigGroup) keys.add(context.keyOf(node.parameters.id))
        for (const children of context.childContexts.values()) for (const child of children) addAll(keys, this.allKeysBelow(child))
        return keys
    }

    private memoized(
        memo: Map<AnalysisContext, Map<TemplateNode, Set<string>>>,
        kind: string,
        node: TemplateNode,
        context: AnalysisContext,
        compute: () => Set<string>,
    ): Set<string> {
        let perContext = memo.get(context)
        if (!perContext) {
            perContext = new Map()
            memo.set(context, perContext)
        }
        const cached = perContext.get(node)
        if (cached) return cached

        const token = `${kind}:${this.contextIds.get(context)}:${node.instanceId}`
        if (this.inProgress.has(token)) throw new UnsupportedVariationStructureError(`cyclic dependency through ${node.nodeClass}`)
        this.inProgress.add(token)
        try {
            const result = compute()
            perContext.set(node, result)
            return result
        } finally {
            this.inProgress.delete(token)
        }
    }
}

function isSceneRoot(node: TemplateNode) {
    return node instanceof ObjectNode || node instanceof HdriLight || node instanceof Render || node instanceof SceneProperties || node instanceof RigidRelation
}

function isUnmodelledVisitor(node: TemplateNode) {
    if (node instanceof ConfigGroup || node instanceof ConfigVariant || node instanceof Group) return false
    return isActivationVisitorNode(node) || isNodeOwnerLikeNode(node)
}

function addAll<T>(target: Set<T>, source: Iterable<T>) {
    for (const item of source) target.add(item)
}
